/**
 * Pure currency-conversion helpers used by WP4. Kept in its own file
 * (no JSX, no React) so Node's test runner can exercise them directly.
 */

function resolveCurrency(currency) {
  return currency || "EUR";
}

/**
 * Convert an EUR amount to the target currency at the given rate.
 *
 * EUR always returns the input unchanged, regardless of rate (there is
 * never a rate dependency on the EUR path).
 *
 * For any OTHER currency (GBP, PLN, …), a missing/zero/negative rate
 * returns `null` — an explicit "cannot convert right now" signal — never
 * the raw EUR number. This used to fail OPEN (`return num`), which silently
 * rendered the unconverted EUR figure under a foreign-currency label: e.g.
 * a PLN visitor saw "59 zł" when "262,25 zł" (rate ≈4.4) was correct — a
 * 77.5% understatement nobody could see was wrong. See handover
 * 2026-07-25-fx-uitval-render-en-charge-pad.md for the measured impact and
 * taak #35 for this fix. Every caller of `convertEurTo` MUST treat `null`
 * as "unknown, do not display or charge a number" — see format-price.js
 * (renders an explicit "price unavailable" state) and StripeCheckout.js /
 * VoucherCheckoutClient.js (block the charge instead of submitting a
 * guessed amount).
 */
export function convertEurTo(value, currency, rate) {
  const num = Number(value) || 0;
  const resolved = resolveCurrency(currency);
  if (resolved === "EUR") return num;
  if (!rate || rate <= 0) return null;
  return num * rate;
}

/** Rounds a convertEurTo(...) result to clean minor units for GBP/PLN. Passes `null` (rate unavailable) straight through — never coerces it to 0/NaN, which would silently look like a real price. */
export function snapConvertedPrice(value, currency, { wholeSource = false } = {}) {
  if (value === null || value === undefined) return null;
  const num = Number(value) || 0;
  const ccy = resolveCurrency(currency);
  // GBP and PLN are converted from the EUR base, so round to clean minor units
  // (pence / grosze) — no fractional-cent amounts reach the Stripe charge or the
  // sticker. EUR is the source currency and never needs snapping.
  if (ccy !== "GBP" && ccy !== "PLN") return num;
  return wholeSource ? Math.round(num) : Math.round(num * 100) / 100;
}

// Kartik #3 (2026-04-25): GBP "price snapping" — the displayed price
// always ends in 9 (149, 159, 169, …). Snaps UP, never down past the
// input, so the markup-buffered amount we collect is preserved or
// strengthened. Already-snapped values are no-ops.
//
// Examples Kartik called out: 154.72 → 159, 133.88 → 139.
// Edge cases: 159 → 159 (no change), 159.01 → 169, 5 → 9, 0 → 0.
//
// Taak #35: passes `null` (a convertEurTo rate-unavailable result) straight
// through instead of coercing it to 0. The GBP strikethrough call sites
// that chain this into computeOriginalPriceFromDiscount(...) already ended
// up null-safe in practice either way (that function's own `now <= 0`
// guard already rejects the coerced 0), but a "snap" helper silently
// turning "unknown" into a real-looking 0 is the same fail-open pattern
// this whole taak removes elsewhere — kept consistent with
// snapConvertedPrice's null passthrough above, in case a future caller
// ever uses this result directly instead of via that specific chain.
export function snapToNineEnding(value) {
  if (value === null || value === undefined) return null;
  const num = Number(value) || 0;
  if (num <= 0) return 0;
  // Smallest n*10 + 9 that is >= num.
  const n = Math.ceil((num - 9) / 10);
  return n * 10 + 9;
}
